import type { RelatedNoteWithCreatedAtAndUpdatedAtAsNumber } from '../Notes/types';
import { z } from 'zod';

/**
 * The recurrence end date field on a shift is not nullable,
 * however, the default value is a value far into the future
 * which is effectively "null".
 *
 * Compare recurrence_end_date with this value to work out whether
 * it has been set or not
 */
export const recurrenceEndDateDefaultValue =
  '9999-12-31T00:00:00+00:00' as const;

/**
 * A Shift for a PRAS Team's roster.
 * This is client application internal model.
 */
export type Shift = {
  readonly endTime: string;
  readonly id: string;
  readonly isDerived: boolean;
  readonly isRecurring: boolean;
  readonly relatedNotes: readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[];
  readonly originalShiftId?: string;
  /**
   * Indicates if this is an overflow shift from previous date range, which will be rendered differently on the roster.
   */
  readonly overflow?: boolean;
  readonly recordStatus: string;
  readonly recurrenceEndDate: string;
  readonly recurrencePattern?: string;
  readonly rootId?: string;
  readonly startTime: string;
  readonly status: string;
};

export type SimpleUser = {
  readonly userId: string;
  readonly firstName: string | undefined;
  readonly lastName: string | undefined;
  readonly userName: string;
};

/**
 * A user in a shift that contains basic information.
 * (Used to add user information to a shift)
 * Information derived from a {@link RosterUserRecord}
 */
export type FilledShift = Shift & {
  readonly user?: SimpleUser;
};

/**
 * A user in the roster that contains basic information and shifts with role assignment.
 */
export type RosterUser = {
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email?: string;
  readonly shifts: readonly Shift[];
  readonly userRoleAssignmentId: string;
  readonly externalId: string;
  readonly username: string;
  readonly userType?: string;
  /**
   * If this is set to true, than the user role assignment(s) associated
   * with these shifts have been (soft) deleted.
   *
   * In this case we don't want to show the role assignment on the roster,
   * in the tooltips or the role assignments counter.
   *
   * We just want to use this data to display shifts in the past for auditing purposes.
   */
  readonly historic: boolean;
};

/**
 * A role roster contains all of the users, with their accompanying shifts,
 * that have been assigned to a single role on a team.
 * This is client application internal model.
 */
export type RoleRoster = {
  readonly teamRoleAssignmentId: string;
  readonly roleId: string;
  readonly roleName: string;
  readonly users: readonly RosterUser[];
  readonly vacantShifts?: readonly Shift[];
};

/**
 * Roster table cell value. E.g. title, actual Shift information.
 */
export type RosterCellValue =
  | number
  | string
  | boolean
  | readonly Shift[]
  | readonly RosterUserRecord[]
  | Record<string, readonly Shift[]>
  | undefined;

/**
 * Roster table's sub table row record. A sub table is shown by expanding parent row.
 */
export type RosterUserRecord = {
  readonly key: string;
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly roleName: string;
  readonly userRoleAssignmentId: string;
  readonly user_name: string;
  readonly user_type?: string | null;
  readonly historic: boolean;
} & Record<string, RosterCellValue>;

/**
 * Roster table's row record. This represents a parent row's record, which may include children (sub rows).
 */
export type RosterTableRowRecord = {
  readonly key: string;
  readonly roleName: string;
  readonly rolePriority: number;
  readonly roleGroup: string;
  readonly roleGroupPriority: number;
  readonly firstColumnData: string;
  readonly teamRoleAssignmentId: string;
  readonly users?: readonly RosterUserRecord[];
  readonly vacantShifts?: Record<string, readonly Shift[]>;
} & Record<string, RosterCellValue>;

/**
 * Available roster's display modes.
 */
export type RosterDisplayMode = 'weekly' | 'monthly';

/**
 * The table cell record of the Shift passed to the modal.
 */
export type ShiftCellRecord = {
  readonly roleName: string;
  readonly selectedDate: Date;
  readonly teamRoleAssignmentId: string;
  readonly userRoleAssignmentId?: string;
  readonly userId?: string;
  readonly firstName?: string;
  readonly lastName?: string;
};

/**
 * Provides two numbers representing the start and end time of a shift.
 * These numbers are assumed to be relative lengths of milliseconds since midnight
 */
export type ShiftPeriod = {
  readonly start: number;
  readonly end: number;
};

export const shiftComponentTopOffset = 0;

export type ShiftPeriodLengths = {
  readonly day: number;
  readonly night: number;
};

export type IdentifiedShiftPeriod = {
  readonly length: number;
  readonly isDayShift: boolean;
};

export type ShiftPeriodType = 'early' | 'day' | 'late';

export type ShiftBackgroundColours = {
  readonly day: string;
  readonly night: string;
  readonly vacantDay: string;
  readonly vacantNight: string;
  readonly violation: string;
};

export type RosterData = readonly {
  readonly teamId: string;
  readonly rosterShiftsRecords: readonly RosterTableRowRecord[];
  readonly rosterColumnHeaders: readonly RosterColumnHeader[];
  readonly dayShiftPeriod?: ShiftPeriod;
  readonly costObjectId?: string;
}[];

export type RosterColumnHeader = {
  readonly title: React.ReactElement;
  readonly isToday: boolean;
  readonly dataIndex: string;
  readonly isWeekend: boolean;
  readonly isLastColumn: boolean;
  readonly link: React.ReactElement;
};

export type ViolationColumnHeader = {
  readonly title: React.ReactElement;
  readonly isToday: boolean;
  readonly dataIndex: string;
  readonly isWeekend: boolean;
  readonly isLastColumn: boolean;
};

export type ColumnHeaderProps = {
  readonly teamId: string;
  readonly rosterDates: readonly Date[];
  readonly personalRosterMode: boolean;
  readonly teamName?: string;
  readonly isArchived: boolean;
};

export type RoleGroupPriority = {
  readonly roleGroup: string;
  readonly roleGroupPriority: number;
  readonly colour: string;
  readonly textColour?: string;
};

export type RolePriority = {
  readonly role: string;
  readonly subPriority: number;
  readonly roleGroup: string;
};

/**
 * Provides the id of a team role, and the name of the role group that the team role is within
 */
export type RoleInformation = {
  readonly name: string;
  readonly teamRoleId: string;
  readonly roleGroup: string;
  readonly colour: string;
  readonly roleGroupPriority: number;
  readonly textColour?: string;
};

export type TeamRoleIdAndGroup = {
  readonly teamRoleId: string;
  readonly roleGroup: string;
};

/**
 * The frequency options of recurrent shift in PRAS.
 */
export const shiftFreqOption = z.enum(['MONTHLY', 'WEEKLY', 'DAILY', 'CUSTOM']);
export type ShiftFreqOption = z.infer<typeof shiftFreqOption>;
